import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FireIcon,
  ClockIcon,
  BoltIcon,
  ChartBarIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  EyeIcon,
  SparklesIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

// Mock data para subastas en vivo
const mockLiveAuctions = [
  {
    id: 1,
    name: 'Equus Dualis #21',
    collection: 'Equus Dualis II',
    image: '/Equus Dualis/Equus Dualis II ) 21.png',
    currentBid: 89.5,
    startPrice: 75,
    currency: 'ETH',
    endTime: new Date(Date.now() + 1800000), // 30 min
    totalBids: 47,
    watchers: 234,
    bidHistory: [
      { bidder: '0x1234...5678', amount: 89.5, time: '2 min ago' },
      { bidder: '0xabcd...ef01', amount: 87, time: '5 min ago' },
      { bidder: '0x9876...5432', amount: 85, time: '8 min ago' },
    ],
    auctionType: 'English',
    isHot: true,
    percentIncrease: 19,
  },
  {
    id: 2,
    name: 'Warrior Mythology #47',
    collection: 'Warrior Mythology',
    image: '/warrior mythology/warrior mythology47.jpg',
    currentBid: 4500,
    startPrice: 4200,
    currency: 'ETH',
    endTime: new Date(Date.now() + 900000), // 15 min
    totalBids: 23,
    watchers: 567,
    bidHistory: [
      { bidder: '0x2222...3333', amount: 4500, time: '1 min ago' },
      { bidder: '0x4444...5555', amount: 4400, time: '3 min ago' },
    ],
    auctionType: 'English',
    isHot: true,
    percentIncrease: 7,
  },
  {
    id: 3,
    name: 'Equus Dualis #13',
    collection: 'Equus Dualis II',
    image: '/Equus Dualis/Equus Dualis II ) 13.png',
    currentBid: 15.2,
    startPrice: 18,
    currency: 'ETH',
    endTime: new Date(Date.now() + 3600000), // 1 hora
    totalBids: 0,
    watchers: 89,
    bidHistory: [],
    auctionType: 'Dutch',
    isHot: false,
    percentIncrease: -15,
  },
  {
    id: 4,
    name: 'Warrior Mythology #69',
    collection: 'Warrior Mythology',
    image: '/warrior mythology/warrior mythology69.jpg',
    currentBid: 7.8,
    startPrice: 6,
    currency: 'ETH',
    endTime: new Date(Date.now() + 300000), // 5 min
    totalBids: 31,
    watchers: 156,
    bidHistory: [
      { bidder: '0x6666...7777', amount: 7.8, time: 'Just now' },
      { bidder: '0x8888...9999', amount: 7.5, time: '1 min ago' },
      { bidder: '0xaaaa...bbbb', amount: 7.2, time: '2 min ago' },
    ],
    auctionType: 'Candle',
    isHot: true,
    percentIncrease: 30,
  },
  {
    id: 5,
    name: 'Equus Dualis #29',
    collection: 'Equus Dualis II',
    image: '/Equus Dualis/Equus Dualis II ) 29.png',
    currentBid: 5.4,
    startPrice: 4.5,
    currency: 'ETH',
    endTime: new Date(Date.now() + 7200000), // 2 horas
    totalBids: 12,
    watchers: 78,
    bidHistory: [
      { bidder: '0xcccc...dddd', amount: 5.4, time: '10 min ago' },
    ],
    auctionType: 'Vickrey',
    isHot: false,
    percentIncrease: 20,
  },
  {
    id: 6,
    name: 'Warrior Mythology #57',
    collection: 'Warrior Mythology',
    image: '/warrior mythology/warrior mythology57.jpg',
    currentBid: 12.3,
    startPrice: 10,
    currency: 'ETH',
    endTime: new Date(Date.now() + 600000), // 10 min
    totalBids: 28,
    watchers: 203,
    bidHistory: [
      { bidder: '0xeeee...ffff', amount: 12.3, time: '30 sec ago' },
      { bidder: '0x1111...2222', amount: 12, time: '1 min ago' },
    ],
    auctionType: 'English',
    isHot: true,
    percentIncrease: 23,
  },
];

const LiveAuctionsPage: React.FC = () => {
  const [auctions, setAuctions] = useState(mockLiveAuctions);
  const [selectedType, setSelectedType] = useState('Todas');
  const [isAutoRefresh, setIsAutoRefresh] = useState(true);

  // Simular actualización en tiempo real
  useEffect(() => {
    if (!isAutoRefresh) return;

    const interval = setInterval(() => {
      setAuctions(prev => prev.map(auction => ({
        ...auction,
        // Simular pequeños cambios
        currentBid: auction.auctionType === 'Dutch'
          ? auction.currentBid - 0.01
          : auction.currentBid + (Math.random() > 0.7 ? Math.random() * 0.5 : 0),
        totalBids: auction.auctionType !== 'Dutch' && Math.random() > 0.8
          ? auction.totalBids + 1
          : auction.totalBids,
      })));
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoRefresh]);

  const formatTimeRemaining = (endTime: Date) => {
    const now = new Date();
    const diff = endTime.getTime() - now.getTime();

    if (diff <= 0) return { text: 'Finalizada', isUrgent: true };

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    if (hours > 0) {
      return { text: `${hours}h ${minutes}m ${seconds}s`, isUrgent: false };
    }
    if (minutes > 10) {
      return { text: `${minutes}m ${seconds}s`, isUrgent: false };
    }
    return { text: `${minutes}m ${seconds}s`, isUrgent: true };
  };

  const getAuctionTypeColor = (type: string) => {
    switch (type) {
      case 'English': return 'bg-green-500/20 text-green-400';
      case 'Dutch': return 'bg-orange-500/20 text-orange-400';
      case 'Vickrey': return 'bg-purple-500/20 text-purple-400';
      case 'Candle': return 'bg-yellow-500/20 text-yellow-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const filteredAuctions = selectedType === 'Todas'
    ? auctions
    : auctions.filter(a => a.auctionType === selectedType);

  // Ordenar por urgencia (terminan pronto primero)
  const sortedAuctions = [...filteredAuctions].sort(
    (a, b) => a.endTime.getTime() - b.endTime.getTime()
  );

  const hotAuctions = sortedAuctions.filter(a => a.isHot);
  const regularAuctions = sortedAuctions.filter(a => !a.isHot);

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero con stats en vivo */}
      <section className="relative py-12 bg-gradient-to-b from-dark-900 to-dark-950 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5" />
        <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
              </span>
              <span className="text-red-400 font-semibold uppercase tracking-wider text-sm">En Vivo</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Subastas <span className="gradient-text">en Vivo</span>
            </h1>
            <p className="text-xl text-gray-400 mb-8">
              Participa en subastas activas en tiempo real
            </p>

            {/* Stats en tiempo real */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
              <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl p-4 border border-dark-700">
                <div className="flex items-center justify-center gap-2 text-primary-400 mb-1">
                  <FireIcon className="w-5 h-5" />
                  <span className="text-2xl font-bold">{auctions.length}</span>
                </div>
                <p className="text-gray-400 text-sm">Subastas Activas</p>
              </div>
              <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl p-4 border border-dark-700">
                <div className="flex items-center justify-center gap-2 text-green-400 mb-1">
                  <BoltIcon className="w-5 h-5" />
                  <span className="text-2xl font-bold">{auctions.reduce((sum, a) => sum + a.totalBids, 0)}</span>
                </div>
                <p className="text-gray-400 text-sm">Pujas Totales</p>
              </div>
              <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl p-4 border border-dark-700">
                <div className="flex items-center justify-center gap-2 text-yellow-400 mb-1">
                  <EyeIcon className="w-5 h-5" />
                  <span className="text-2xl font-bold">{auctions.reduce((sum, a) => sum + a.watchers, 0)}</span>
                </div>
                <p className="text-gray-400 text-sm">Observadores</p>
              </div>
              <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl p-4 border border-dark-700">
                <div className="flex items-center justify-center gap-2 text-purple-400 mb-1">
                  <ChartBarIcon className="w-5 h-5" />
                  <span className="text-2xl font-bold">
                    {auctions.reduce((sum, a) => sum + a.currentBid, 0).toFixed(1)}
                  </span>
                </div>
                <p className="text-gray-400 text-sm">ETH en Juego</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filtros y controles */}
      <section className="py-6 border-b border-dark-800 sticky top-20 bg-dark-950/90 backdrop-blur-sm z-30">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Filtros por tipo */}
            <div className="flex flex-wrap gap-2">
              {['Todas', 'English', 'Dutch', 'Vickrey', 'Candle'].map((type) => (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedType === type
                      ? 'bg-primary-500 text-white'
                      : 'bg-dark-800 text-gray-400 hover:text-white hover:bg-dark-700'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>

            {/* Auto-refresh toggle */}
            <button
              onClick={() => setIsAutoRefresh(!isAutoRefresh)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-colors ${
                isAutoRefresh
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-dark-800 text-gray-400'
              }`}
            >
              <ArrowPathIcon className={`w-4 h-4 ${isAutoRefresh ? 'animate-spin' : ''}`} />
              Auto-refresh {isAutoRefresh ? 'ON' : 'OFF'}
            </button>
          </div>
        </div>
      </section>

      {/* Subastas Destacadas / Hot */}
      {hotAuctions.length > 0 && (
        <section className="py-8">
          <div className="container mx-auto px-4">
            <div className="flex items-center gap-2 mb-6">
              <FireIcon className="w-6 h-6 text-orange-500" />
              <h2 className="text-2xl font-bold text-white">Subastas Calientes</h2>
              <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-full">
                Alta actividad
              </span>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {hotAuctions.slice(0, 2).map((auction, idx) => {
                const timeInfo = formatTimeRemaining(auction.endTime);
                return (
                  <motion.div
                    key={auction.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="bg-dark-800/50 rounded-2xl border border-dark-700 overflow-hidden hover:border-primary-500/50 transition-all"
                  >
                    <div className="flex">
                      {/* Imagen */}
                      <Link to={`/auction/${auction.id}`} className="w-1/3 relative">
                        <img
                          src={auction.image}
                          alt={auction.name}
                          className="w-full h-full object-cover"
                        />
                        <div className={`absolute top-3 left-3 px-2 py-1 rounded-lg text-xs font-medium ${getAuctionTypeColor(auction.auctionType)}`}>
                          {auction.auctionType}
                        </div>
                      </Link>

                      {/* Info */}
                      <div className="flex-1 p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <p className="text-gray-500 text-sm">{auction.collection}</p>
                            <Link to={`/auction/${auction.id}`}>
                              <h3 className="text-xl font-bold text-white hover:text-primary-400 transition-colors">
                                {auction.name}
                              </h3>
                            </Link>
                          </div>
                          <div className={`flex items-center gap-1 px-3 py-1.5 rounded-lg ${
                            timeInfo.isUrgent ? 'bg-red-500/20 text-red-400' : 'bg-dark-700 text-white'
                          }`}>
                            <ClockIcon className="w-4 h-4" />
                            <span className="font-mono font-medium">{timeInfo.text}</span>
                          </div>
                        </div>

                        {/* Precio y stats */}
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <p className="text-gray-500 text-sm">Puja Actual</p>
                            <p className="text-2xl font-bold text-white">
                              {auction.currentBid.toFixed(2)} <span className="text-gray-400 text-sm">{auction.currency}</span>
                            </p>
                            <p className={`text-sm flex items-center gap-1 ${
                              auction.percentIncrease >= 0 ? 'text-green-400' : 'text-red-400'
                            }`}>
                              <ArrowTrendingUpIcon className="w-4 h-4" />
                              {auction.percentIncrease >= 0 ? '+' : ''}{auction.percentIncrease}% desde inicio
                            </p>
                          </div>
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2 text-gray-400">
                              <BoltIcon className="w-4 h-4" />
                              <span>{auction.totalBids} pujas</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-400">
                              <EyeIcon className="w-4 h-4" />
                              <span>{auction.watchers} observando</span>
                            </div>
                          </div>
                        </div>

                        {/* Últimas pujas */}
                        {auction.bidHistory.length > 0 && (
                          <div className="border-t border-dark-700 pt-4">
                            <p className="text-xs text-gray-500 mb-2">Últimas pujas:</p>
                            <div className="space-y-1">
                              {auction.bidHistory.slice(0, 2).map((bid, bidIdx) => (
                                <div key={bidIdx} className="flex items-center justify-between text-sm">
                                  <span className="text-gray-400">{bid.bidder}</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-white font-medium">{bid.amount} ETH</span>
                                    <span className="text-gray-500 text-xs">{bid.time}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* CTA */}
                        <Link
                          to={`/auction/${auction.id}`}
                          className="mt-4 block w-full py-3 bg-primary-500 hover:bg-primary-600 text-white text-center rounded-xl font-medium transition-colors"
                        >
                          Participar Ahora
                        </Link>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Lista de todas las subastas */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-white mb-6">Todas las Subastas en Vivo</h2>

          <div className="space-y-4">
            {sortedAuctions.map((auction, idx) => {
              const timeInfo = formatTimeRemaining(auction.endTime);
              return (
                <motion.div
                  key={auction.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-dark-800/50 rounded-xl border border-dark-700 p-4 hover:border-primary-500/50 transition-all"
                >
                  <div className="flex items-center gap-4">
                    {/* Imagen */}
                    <Link to={`/auction/${auction.id}`} className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={auction.image}
                        alt={auction.name}
                        className="w-full h-full object-cover hover:scale-110 transition-transform"
                      />
                    </Link>

                    {/* Info principal */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getAuctionTypeColor(auction.auctionType)}`}>
                          {auction.auctionType}
                        </span>
                        {auction.isHot && (
                          <span className="flex items-center gap-1 px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded">
                            <FireIcon className="w-3 h-3" />
                            Hot
                          </span>
                        )}
                        <span className="text-gray-500 text-sm">{auction.collection}</span>
                      </div>
                      <Link to={`/auction/${auction.id}`}>
                        <h3 className="text-white font-semibold truncate hover:text-primary-400 transition-colors">
                          {auction.name}
                        </h3>
                      </Link>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <BoltIcon className="w-4 h-4" />
                          {auction.totalBids} pujas
                        </span>
                        <span className="flex items-center gap-1">
                          <EyeIcon className="w-4 h-4" />
                          {auction.watchers}
                        </span>
                      </div>
                    </div>

                    {/* Precio */}
                    <div className="text-right">
                      <p className="text-gray-500 text-xs">Puja Actual</p>
                      <p className="text-xl font-bold text-white">
                        {auction.currentBid.toFixed(2)} <span className="text-gray-400 text-sm">{auction.currency}</span>
                      </p>
                    </div>

                    {/* Tiempo */}
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                      timeInfo.isUrgent ? 'bg-red-500/20 text-red-400' : 'bg-dark-700 text-white'
                    }`}>
                      <ClockIcon className="w-5 h-5" />
                      <span className="font-mono font-medium min-w-[80px] text-center">{timeInfo.text}</span>
                    </div>

                    {/* CTA */}
                    <Link
                      to={`/auction/${auction.id}`}
                      className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors whitespace-nowrap"
                    >
                      Ver Subasta
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {sortedAuctions.length === 0 && (
            <div className="text-center py-16">
              <SparklesIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">
                No hay subastas activas de este tipo
              </h3>
              <p className="text-gray-400 mb-6">
                Prueba con otro filtro o vuelve más tarde
              </p>
              <button
                onClick={() => setSelectedType('Todas')}
                className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
              >
                Ver todas las subastas
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA para usuarios no conectados */}
      <section className="py-16 bg-gradient-to-r from-primary-500/10 to-orange-500/10">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              ¿Listo para participar?
            </h2>
            <p className="text-gray-300 mb-8">
              Conecta tu wallet para empezar a pujar en cualquiera de las subastas activas.
              Obtén beneficios exclusivos al stakear NDH tokens.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/how-it-works"
                className="px-8 py-3 bg-dark-800 hover:bg-dark-700 text-white rounded-xl transition-colors"
              >
                Cómo Funciona
              </Link>
              <Link
                to="/explore"
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors"
              >
                Explorar Todas
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LiveAuctionsPage;
